<template>
  <div>
    <!-- Mobile Menu Overlay -->
    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
        @click="$emit('close')"
      />
    </Transition>

    <!-- Mobile Slide-out Menu -->
    <Transition
      enter-active-class="transition-transform duration-300 ease-out"
      enter-from-class="-translate-y-full"
      enter-to-class="translate-y-0"
      leave-active-class="transition-transform duration-300 ease-in"
      leave-from-class="translate-y-0"
      leave-to-class="-translate-y-full"
    >
      <div
        v-if="isOpen"
        class="fixed top-0 left-0 right-0 bg-white shadow-2xl z-50 md:hidden rounded-b-3xl border-b border-gray-200"
        data-test="mobile-menu"
      >
        <!-- Mobile Menu Header -->
        <div class="flex items-center justify-between px-5 pt-5 pb-4">
          <span class="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            {{ t('common.menu') }}
          </span>
          <button
            class="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors active:scale-90"
            @click="$emit('close')"
          >
            <PhX :size="24" weight="bold" />
          </button>
        </div>
        <!-- Mobile Menu Content -->
        <div class="px-4 pb-6 space-y-3">
          <!-- Radar Link -->
          <NuxtLink
            to="/radar"
            class="flex items-center gap-3 px-4 py-3.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all active:scale-95"
            data-test="mobile-radar-link"
            @click="$emit('close')"
          >
            <div class="flex items-center justify-center w-10 h-10 bg-gray-50 rounded-lg">
              <PhTarget :size="20" class="text-green-600" weight="bold" />
            </div>
            <span class="font-medium text-gray-700">{{ t('common.radar') }}</span>
          </NuxtLink>
          <!-- Divider -->
          <div class="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-4" />
          <!-- Language Section -->
          <div class="space-y-2">
            <p class="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {{ t('common.language') }}
            </p>
            <div class="grid grid-cols-2 gap-2">
              <button
                :class="[
                  'relative px-4 py-3 rounded-lg text-sm font-semibold transition-all active:scale-95 overflow-hidden',
                  $i18n.locale.value === 'fa'
                    ? 'text-white shadow-md'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                ]"
                @click="$emit('language-change', 'fa')"
              >
                <span class="relative z-10">{{ t('common.persian') }}</span>
                <div
                  v-if="$i18n.locale.value === 'fa'"
                  class="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600"
                />
              </button>
              <button
                :class="[
                  'relative px-4 py-3 rounded-lg text-sm font-semibold transition-all active:scale-95 overflow-hidden',
                  $i18n.locale.value === 'en'
                    ? 'text-white shadow-md'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                ]"
                @click="$emit('language-change', 'en')"
              >
                <span class="relative z-10">{{ t('common.english') }}</span>
                <div
                  v-if="$i18n.locale.value === 'en'"
                  class="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600"
                />
              </button>
            </div>
          </div>
          <!-- Divider -->
          <div class="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-4" />
          <!-- Theme Section -->
          <div class="space-y-2">
            <p class="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {{ t('common.appearance') }}
            </p>
            <button
              class="w-full flex items-center justify-between px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-all active:scale-95"
              @click="$emit('theme-toggle')"
            >
              <span class="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <PhMoonStars v-if="!isDark" :size="18" />
                <PhSun v-else :size="18" />
                {{ isDark ? t('common.lightMode') : t('common.darkMode') }}
              </span>
            </button>
          </div>
          <!-- Divider -->
          <div class="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-4" />
          <!-- Auth Button -->
          <button
            v-if="isLoggedIn"
            class="w-full px-4 py-3.5 text-center text-sm font-semibold text-gray-700 dark:text-slate-200 bg-gray-50 dark:bg-slate-800/80 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/80 transition-all active:scale-95"
            data-test="mobile-logout-button"
            @click="$emit('logout')"
          >
            {{ t('auth.logout') }}
          </button>
          <NuxtLink
            v-else
            to="/login"
            class="relative block w-full px-4 py-3.5 text-sm text-center font-semibold text-white rounded-lg shadow-md transition-all active:scale-95 overflow-hidden"
            data-test="mobile-login-button"
            @click="$emit('close')"
          >
            <span class="relative z-10">{{ t('auth.login') }}</span>
            <div class="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600" />
          </NuxtLink>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '#i18n'
import { PhTarget, PhX, PhMoonStars, PhSun } from '@phosphor-icons/vue'

const { t } = useI18n()
const { $i18n } = useNuxtApp()

defineProps<{
  isOpen: boolean
  isLoggedIn: boolean
  isDark: boolean
}>()

defineEmits<{
  close: []
  logout: []
  'theme-toggle': []
  'language-change': [locale: 'fa' | 'en']
}>()
</script>
